/**
 * ConfigureConnectionDialog — Configuração do node "Conexão"
 * -----------------------------------------------------------------------------
 * Permite escolher uma conexão ativa do Integration Hub, o recurso (operação)
 * habilitado nela e preencher os parâmetros dinamicamente a partir do
 * `params_schema` do catálogo — aceitando variáveis do fluxo ({{telefone}}).
 *
 * A consulta NÃO roda aqui: em execução o fluxo apenas enfileira e o worker
 * devolve o resultado em variáveis.
 *
 * RELEASE: 1.3.0 - 2026-08-29 - Retorno do teste exibido no JsonExplorer
 *          (árvore/JSON/tabela) com quebra de linha, busca e mapeamento direto
 *          do campo para variável; modal mais largo e linhas de mapeamento em
 *          grid responsivo.
 * RELEASE: 1.4.0 - 2026-08-29 - Aviso de consulta assíncrona no teste (provider
 *          respondeu "em processamento"; a fila repete a chamada em produção).
 * RELEASE: 1.5.0 - 2026-09-07 - Retorno migrado para o modal de destino.
 * RELEASE: 1.2.0 - 2026-08-29 - Seção "Testar consulta" (padrão do node Webhook):
 *          executa a operação real, mostra o payload retornado e permite mapear
 *          qualquer caminho do JSON para variáveis do fluxo (provider-agnóstico).
 * RELEASE: 1.1.0 - 2026-08-29 - Layout padronizado com o node "Gerar Contrato":
 *          cabeçalho com ícone, seções com Separator, InputWithVariables nos
 *          parâmetros, VariablePickerCombobox na variável de retorno, avisos
 *          via InlineNotice e pré-preenchimento dos valores padrão do schema.
 * RELEASE: 1.0.0 - 2026-08-29 - Versão inicial (Fase 2 da Fila de Integrações).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Plug, Boxes, SlidersHorizontal, ToggleRight, Save, PlayCircle, Loader2, Plus, Trash2, FileText } from 'lucide-react';
import { InputWithVariables } from '../InputWithVariables';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { useAuth } from '@/hooks/useAuth';
import { useIntegrationConnections } from '@/hooks/integrations/useIntegrationConnections';
import { useConnectionTest } from '@/hooks/integrations/useConnectionTest';
import { getValueByPath, stringifyPathValue, suggestVariableName } from '@/lib/flow/jsonPaths';
import { JsonExplorer } from './JsonExplorer';
import type { ConnectionQueryConfig, ResponseFieldMapping } from '@/types/flows';

/** Campo declarado no params_schema da operação */
interface ParamField {
  key: string;
  type?: string;
  label?: string;
  required?: boolean;
  help?: string;
  default?: unknown;
}

interface ConfigureConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: { id: string; data: { config?: unknown } } | null;
  onSave: (nodeId: string, config: Record<string, unknown>) => void;
}

export function ConfigureConnectionDialog({
  open,
  onOpenChange,
  node,
  onSave,
}: ConfigureConnectionDialogProps) {
  const config = (node?.data?.config ?? {}) as ConnectionQueryConfig;

  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? '';

  const { connections, providers } = useIntegrationConnections();

  const [nickname, setNickname] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [operation, setOperation] = useState('');
  const [resultVariable, setResultVariable] = useState('consulta');
  const [resultVariableId, setResultVariableId] = useState('');
  const [values, setValues] = useState<Record<string, unknown>>({});

  // RELEASE 1.2.0 — teste da consulta e mapeamento genérico do retorno
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [responseMapping, setResponseMapping] = useState<ResponseFieldMapping[]>([]);
  const { isLoading: isTesting, result: testResult, runTest, clearResult } = useConnectionTest();

  /** Somente conexões ativas podem ser usadas em automação */
  const activeConnections = useMemo(
    () => (connections ?? []).filter((c) => c.is_active),
    [connections],
  );

  const selectedConnection = useMemo(
    () => activeConnections.find((c) => c.id === connectionId) ?? null,
    [activeConnections, connectionId],
  );

  /** Recursos habilitados na conexão escolhida (consulta ao vivo) */
  const availableOperations = useMemo(() => {
    if (!selectedConnection) return [];
    const enabledCodes = new Set(
      (selectedConnection.enabled_operations ?? [])
        .filter((op) => op.is_enabled && op.is_live_query)
        .map((op) => op.code),
    );
    const provider = (providers ?? []).find(
      (p) => p.slug === selectedConnection.provider_slug,
    );
    return (provider?.operations ?? []).filter((op) => enabledCodes.has(op.code));
  }, [selectedConnection, providers]);

  const selectedOperation = useMemo(
    () => availableOperations.find((op) => op.code === operation) ?? null,
    [availableOperations, operation],
  );

  /** Campos dinâmicos do recurso escolhido */
  const fields: ParamField[] = useMemo(() => {
    const schema = (selectedOperation as { params_schema?: unknown } | null)?.params_schema;
    if (!Array.isArray(schema)) return [];
    return schema.filter((f): f is ParamField => Boolean(f && typeof f === 'object' && 'key' in f));
  }, [selectedOperation]);

  /** Separa campos de texto dos toggles booleanos (agrupados em "Opções") */
  const textFields = useMemo(() => fields.filter((f) => f.type !== 'boolean'), [fields]);
  const booleanFields = useMemo(() => fields.filter((f) => f.type === 'boolean'), [fields]);

  /** Monta os valores iniciais de um recurso usando os defaults do schema */
  const buildDefaults = (schemaFields: ParamField[]): Record<string, unknown> => {
    const initial: Record<string, unknown> = {};
    schemaFields.forEach((f) => {
      if (f.default !== undefined) initial[f.key] = f.default;
    });
    return initial;
  };

  /** Extrai os campos do params_schema de uma operação arbitrária */
  const schemaOf = (op: unknown): ParamField[] => {
    const schema = (op as { params_schema?: unknown } | null)?.params_schema;
    if (!Array.isArray(schema)) return [];
    return schema.filter((f): f is ParamField => Boolean(f && typeof f === 'object' && 'key' in f));
  };

  /** Carrega a configuração salva ao abrir */
  useEffect(() => {
    if (!open) return;
    setNickname(config.nickname ?? '');
    setConnectionId(config.connection_id ?? '');
    setOperation(config.operation ?? '');
    setResultVariable(config.result_variable || 'consulta');
    setResultVariableId(config.result_variable_id ?? '');
    setValues((config.params ?? {}) as Record<string, unknown>);
    setTestValues((config.test_values ?? {}) as Record<string, string>);
    setResponseMapping((config.response_mapping ?? []) as ResponseFieldMapping[]);
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, node?.id]);

  /** Obrigatórios que ainda estão vazios (bloqueiam o salvamento) */
  const missingRequired = useMemo(
    () =>
      fields
        .filter((f) => f.required && f.type !== 'boolean')
        .filter((f) => String(values[f.key] ?? '').trim() === '')
        .map((f) => f.label ?? f.key),
    [fields, values],
  );

  /**
   * Monta os parâmetros do teste: usa o valor configurado quando ele é fixo e o
   * valor de teste quando o campo contém variáveis ({{...}}), pois no builder
   * não existe contexto de execução para interpolar.
   */
  const buildTestParams = (): Record<string, unknown> => {
    const params: Record<string, unknown> = {};
    fields.forEach((field) => {
      const configured = values[field.key];
      if (field.type === 'boolean') {
        params[field.key] = Boolean(configured);
        return;
      }
      const asText = String(configured ?? '');
      params[field.key] = asText.includes('{{') ? (testValues[field.key] ?? '') : asText;
    });
    return params;
  };

  const handleTest = () => {
    if (!connectionId || !operation) return;
    void runTest({ connectionId, operation, params: buildTestParams() });
  };

  /** Adiciona/remove/edita linhas do mapeamento genérico */
  const addMapping = (path = '') => {
    setResponseMapping((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        responsePath: path,
        // Sugere um nome de variável a partir do caminho clicado (padrão n8n)
        variableName: path ? suggestVariableName(path) : '',
      },
    ]);
  };

  const updateMapping = (id: string, patch: Partial<ResponseFieldMapping>) => {
    setResponseMapping((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const removeMapping = (id: string) => {
    setResponseMapping((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSave = () => {
    if (!node) return;

    onSave(node.id, {
      nickname: nickname.trim() || undefined,
      connection_id: connectionId,
      connection_name: selectedConnection?.name ?? null,
      provider_slug: selectedConnection?.provider_slug ?? null,
      operation,
      operation_name: selectedOperation?.name ?? null,
      params: values,
      result_variable: resultVariable.trim() || 'consulta',
      result_variable_id: resultVariableId || undefined,
      // Mapeamento genérico (padrão n8n): caminho no payload → variável do fluxo
      response_mapping: responseMapping
        .filter((m) => m.responsePath.trim() && m.variableName.trim())
        .slice(0, 50),
      test_values: testValues,
    });

    onOpenChange(false);
  };


  const canSave = Boolean(connectionId && operation) && missingRequired.length === 0;
  const varName = resultVariable || 'consulta';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* RELEASE: 2026-08-29 v1.1.1 - Corrige corte das bordas dos campos: padding movido
          para dentro do conteúdo rolável (scrollbar não encosta mais nos inputs). */}
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-sky-500">
              <Plug className="h-4 w-4 text-white" />
            </div>
            Configurar Conexão
          </DialogTitle>
          <DialogDescription>
            Consulte um aplicativo integrado e use o retorno nos próximos passos do fluxo.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-6 px-6 py-4">
            {/* ============================================ */}
            {/* SEÇÃO 1: Apelido do node */}
            {/* ============================================ */}
            <div className="space-y-2">
              <Label>Apelido do node (opcional)</Label>
              <Input
                value={nickname}
                maxLength={60}
                placeholder="Ex.: Consulta de crédito"
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 2: Conexão (aplicativo integrado) */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Plug className="h-4 w-4 text-sky-500" />
                <Label className="text-base font-semibold">Aplicativo conectado</Label>
              </div>

              <Select
                value={connectionId}
                onValueChange={(value) => {
                  setConnectionId(value);
                  setOperation('');
                  setValues({});
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {activeConnections.map((connection) => (
                    <SelectItem key={connection.id} value={connection.id}>
                      <div className="flex items-center gap-2">
                        <span>{connection.name}</span>
                        <span className="text-xs text-muted-foreground">
                          — {connection.provider_name ?? connection.provider_slug}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedConnection && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="text-[10px]">
                    {selectedConnection.provider_name ?? selectedConnection.provider_slug}
                  </Badge>
                  {selectedConnection.is_default && (
                    <Badge variant="outline" className="text-[10px]">Padrão</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {selectedConnection.enabled_operations_count} recurso(s) habilitado(s)
                  </span>
                </div>
              )}

              {activeConnections.length === 0 && (
                <InlineNotice variant="warning">
                  Nenhuma conexão ativa. Configure em Dados &gt; Aplicativos e integrações.
                </InlineNotice>
              )}
            </div>

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 3: Recurso (operação) */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Boxes className="h-4 w-4 text-sky-500" />
                <Label className="text-base font-semibold">Recurso</Label>
              </div>

              <Select
                value={operation}
                onValueChange={(value) => {
                  setOperation(value);
                  const op = availableOperations.find((o) => o.code === value);
                  setValues(buildDefaults(schemaOf(op)));
                }}
                disabled={!connectionId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={connectionId ? 'Selecione o recurso' : 'Selecione a conexão primeiro'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableOperations.map((op) => (
                    <SelectItem key={op.code} value={op.code}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedOperation?.description && (
                <p className="text-xs text-muted-foreground">{selectedOperation.description}</p>
              )}

              {connectionId && availableOperations.length === 0 && (
                <InlineNotice variant="warning">
                  Nenhum recurso de consulta habilitado nesta conexão. Habilite os recursos na aba
                  Recursos do aplicativo.
                </InlineNotice>
              )}
            </div>

            {/* ============================================ */}
            {/* SEÇÃO 4: Parâmetros da consulta */}
            {/* ============================================ */}
            {textFields.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4 text-sky-500" />
                    <Label className="text-base font-semibold">Parâmetros da consulta</Label>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-1">
                    Informe um valor fixo ou insira variáveis do fluxo pelo botão {'{}'}.
                  </p>

                  {textFields.map((field) => (
                    <div key={field.key} className="space-y-1.5">
                      <Label className="text-xs">
                        {field.label ?? field.key}
                        {field.required && <span className="text-destructive"> *</span>}
                      </Label>
                      <InputWithVariables
                        tenantId={tenantId}
                        value={String(values[field.key] ?? '')}
                        maxLength={200}
                        placeholder="Valor fixo ou {{variavel}}"
                        onChange={(value) =>
                          setValues((prev) => ({ ...prev, [field.key]: value }))
                        }
                      />
                      {field.help && (
                        <p className="text-xs text-muted-foreground">{field.help}</p>
                      )}
                    </div>
                  ))}

                  {missingRequired.length > 0 && (
                    <InlineNotice variant="warning">
                      Preencha os campos obrigatórios: {missingRequired.join(', ')}.
                    </InlineNotice>
                  )}
                </div>
              </>
            )}

            {/* ============================================ */}
            {/* SEÇÃO 5: Opções (toggles booleanos) */}
            {/* ============================================ */}
            {booleanFields.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ToggleRight className="h-4 w-4 text-sky-500" />
                    <Label className="text-base font-semibold">Opções</Label>
                  </div>

                  <div className="rounded-lg border border-border divide-y divide-border">
                    {booleanFields.map((field) => (
                      <div
                        key={field.key}
                        className="flex items-center justify-between gap-4 p-3"
                      >
                        <div className="space-y-0.5">
                          <Label className="text-sm">{field.label ?? field.key}</Label>
                          {field.help && (
                            <p className="text-xs text-muted-foreground">{field.help}</p>
                          )}
                        </div>
                        <Switch
                          checked={Boolean(values[field.key])}
                          onCheckedChange={(checked) =>
                            setValues((prev) => ({ ...prev, [field.key]: checked }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* ============================================ */}
            {/* SEÇÃO 6: Variável de retorno */}
            {/* ============================================ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Save className="h-4 w-4 text-sky-500" />
                <Label className="text-base font-semibold">Salvar resultado em variável</Label>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Selecione ou crie a variável que receberá o retorno da consulta
              </p>

              <VariableDestinationPicker
                tenantId={tenantId}
                value={resultVariableId || resultVariable}
                valueName={resultVariable}
                onChange={(variable) => {
                  setResultVariableId(variable?.id || '');
                  setResultVariable(variable?.name || 'consulta');
                }}
                allowClear
                placeholder="Selecione ou crie uma variável"
              />

              <div className="bg-muted/50 rounded p-2 space-y-1">
                <p className="text-xs text-muted-foreground font-medium">
                  Variáveis criadas pela execução:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <code className="bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 px-1.5 py-0.5 rounded text-[10px]">
                    {`{{${varName}}}`}
                  </code>
                  <code className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded text-[10px]">
                    {`{{${varName}_status}}`}
                  </code>
                  <code className="bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded text-[10px]">
                    {`{{${varName}_pdf_url}}`}
                  </code>
                  <code className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded text-[10px]">
                    {`{{${varName}_erro}}`}
                  </code>
                  <code className="bg-muted text-muted-foreground px-1.5 py-0.5 rounded text-[10px]">
                    {`{{${varName}_consulta_id}}`}
                  </code>
                </div>
              </div>
            </div>

            {/* ============================================ */}
            {/* SEÇÃO 7: Testar consulta + mapeamento do retorno (RELEASE 1.2.0) */}
            {/* Provider-agnóstico: os caminhos vêm do retorno real do provider */}
            {/* ============================================ */}
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-sky-500" />
                <Label className="text-base font-semibold">Testar consulta</Label>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Executa a operação real no provider para visualizar o retorno e escolher quais
                campos viram variáveis do fluxo.
              </p>

              {/* Valores de teste para campos que usam variáveis */}
              {textFields
                .filter((f) => String(values[f.key] ?? '').includes('{{'))
                .map((field) => (
                  <div key={`test-${field.key}`} className="space-y-1.5">
                    <Label className="text-xs">
                      Valor de teste — {field.label ?? field.key}
                    </Label>
                    <Input
                      value={testValues[field.key] ?? ''}
                      maxLength={200}
                      placeholder="Valor usado apenas no teste"
                      onChange={(e) =>
                        setTestValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                    />
                  </div>
                ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleTest}
                disabled={!connectionId || !operation || isTesting}
              >
                {isTesting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlayCircle className="h-4 w-4 mr-2" />
                )}
                {isTesting ? 'Consultando...' : 'Testar consulta'}
              </Button>

              {/* Resultado do teste */}
              {testResult && !testResult.success && (
                <InlineNotice variant="error">{testResult.error}</InlineNotice>
              )}

              {/* RELEASE 1.5.0 — consulta assíncrona: o teste mostra apenas o
                  aceite do provider; em produção a fila acompanha o resultado
                  (poll sem custo, backoff 30s→120s, teto de 6 minutos). */}
              {testResult?.success && testResult.pending && (
                <InlineNotice variant="warning">
                  Consulta assíncrona: o provider aceitou o pedido e ainda está processando.
                  Em produção o sistema acompanha o resultado automaticamente (pode levar
                  alguns minutos) e só então grava o retorno e o PDF no histórico.
                  {testResult.pendingMessage ? ` (${testResult.pendingMessage})` : ''}
                </InlineNotice>
              )}

              {testResult?.success && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {testResult.durationMs} ms
                    </Badge>
                    {testResult.hasPdf && (
                      <Badge variant="outline" className="text-[10px] gap-1">
                        <FileText className="h-3 w-3" />
                        PDF disponível
                      </Badge>
                    )}
                  </div>

                  {/* RELEASE 1.3.0 — explorador genérico (árvore/JSON/tabela) com
                      quebra de linha e mapeamento direto do campo → variável */}
                  <JsonExplorer data={testResult.data} onSelectPath={addMapping} />
                </div>
              )}

              {/* Mapeamento campo → variável */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Mapeamento do retorno</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addMapping()}>
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Adicionar
                  </Button>
                </div>

                {responseMapping.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum campo mapeado. O retorno completo continua disponível em{' '}
                    <code>{`{{${varName}}}`}</code>.
                  </p>
                )}

                {responseMapping.map((mapping) => {
                  const rawValue = testResult?.success
                    ? getValueByPath(testResult.data, mapping.responsePath)
                    : undefined;
                  const preview = stringifyPathValue(rawValue);
                  // Caminho não encontrado no payload de teste (apenas aviso)
                  const notFound =
                    Boolean(testResult?.success) &&
                    mapping.responsePath.trim() !== '' &&
                    rawValue === undefined;
                  return (
                    <div key={mapping.id} className="space-y-1.5 rounded border border-border p-2">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
                        <Input
                          value={mapping.responsePath}
                          maxLength={200}
                          placeholder="caminho.no.payload"
                          onChange={(e) => updateMapping(mapping.id, { responsePath: e.target.value })}
                        />
                        <Input
                          value={mapping.variableName}
                          maxLength={60}
                          placeholder="nome_da_variavel"
                          onChange={(e) =>
                            updateMapping(mapping.id, {
                              variableName: e.target.value.replace(/[^\w]/g, '_').toLowerCase(),
                            })
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="justify-self-end"
                          onClick={() => removeMapping(mapping.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      {preview && (
                        <p className="text-[10px] text-muted-foreground line-clamp-3 break-all">
                          Prévia: {preview}
                        </p>
                      )}
                      {notFound && (
                        <p className="text-[10px] text-amber-600 dark:text-amber-400">
                          Caminho não encontrado no retorno do teste.
                        </p>
                      )}
                    </div>
                  );
                })}

              </div>
            </div>
          </div>

        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
